import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { AthleteEnergyExpenditureHistoryQuery } from "../../../../types";
import { TableCountHeader } from "./TableCountHeader";
import { HistoricalTableHeader } from "./HistoricalTableHeader";
import { HistoricalTableRow } from "./HistoricalTableRow";
import { HistoricalTableFooterRow } from "./HistoricalTableFooterRow";
import { parseISO } from "date-fns";
import { useDateFormatting } from "shared-ui/contexts/Locale/useDateFormatting";

interface HistoricalTableProps {
  data?: AthleteEnergyExpenditureHistoryQuery["athleteEnergyExpenditureHistory"] | null;
  onPageChange: (offset: number, limit: number) => void;
  offset: number;
  limit: number;
  athleteId: string;
  teamId: string;
  showEmptyState?: boolean;
}

export const HistoricalTable: React.FC<HistoricalTableProps> = ({
  data,
  onPageChange,
  offset,
  limit,
  athleteId,
  teamId,
  showEmptyState = false,
}) => {
  const { formatDateWithLocale } = useDateFormatting();
  const page = offset / limit;
  const rowsPerPage = limit;

  const handleChangePage = (_event: unknown, newPage: number) => {
    onPageChange(newPage * rowsPerPage, rowsPerPage);
  };

  // Check if all entries on current page have no AMS data (all energy values are null)
  const allEntriesHaveNoData = React.useMemo(() => {
    if (!data?.edges || data.edges.length === 0) return false;
    return data.edges.every(
      edge => edge.tdee === null && edge.activeEnergy === null && edge.restingEnergy === null
    );
  }, [data]);

  // Show empty message if: true empty state OR all entries have no data
  const showEmptyMessage = showEmptyState || allEntriesHaveNoData;

  // Group data by date to handle multiple sources
  const groupedData = React.useMemo(() => {
    if (!data || showEmptyState) return new Map();

    const grouped = new Map<string, Array<typeof data.edges[number]>>();

    for (const edge of data.edges) {
      const date = edge.date;
      if (!grouped.has(date)) {
        grouped.set(date, []);
      }
      grouped.get(date)!.push(edge);
    }

    return grouped;
  }, [data, showEmptyState]);

  const totalCount = data?.pageInfo.total ?? 0;

  // Calculate week range for current page
  const getWeekRange = () => {
    // Use data.edges directly to calculate week range, even if showing empty message
    if (!data?.edges || data.edges.length === 0) return null;

    // Extract all unique dates from edges and sort them
    const dates = Array.from(new Set(data.edges.map(edge => edge.date))).sort();
    if (dates.length === 0) return null;

    // Table shows dates in descending order (newest first, oldest last)
    // So show range as "newest - oldest" to match visual order
    const newestDate = parseISO(dates[dates.length - 1]);
    const oldestDate = parseISO(dates[0]);

    // "PP" is date-fns' locale-aware medium date — "Jan 4, 2025" (en-US) or "4 Jan 2025" (en-GB/AU/NZ).
    const newestFormatted = formatDateWithLocale(newestDate, { formatVariant: "PP" });
    const oldestFormatted = formatDateWithLocale(oldestDate, { formatVariant: "PP" });
    return `${newestFormatted} - ${oldestFormatted}`;
  };

  const weekRange = getWeekRange();

  // Custom label for pagination showing date ranges
  const getLabelDisplayedRows = ({ from, to, count }: { from: number; to: number; count: number }) => {
    // The 'from' and 'to' here represent row indices, not dates
    // We need to show the actual date range instead
    if (weekRange) {
      return weekRange;
    }
    return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
  };

  return (
    <Box>
      <Paper
        sx={{
          border: "1px solid #eaecf0",
          borderRadius: 0.75,
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        <TableCountHeader count={totalCount} />
        <TableContainer sx={{ background: "#ffffff" }}>
          <Table>
            <HistoricalTableHeader />
            <TableBody>
              {showEmptyMessage ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center", py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      {weekRange
                        ? `No historical energy expenditure data available for ${weekRange}`
                        : "No historical energy expenditure data available"
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {Array.from(groupedData.entries()).map(([date, entries]) => (
                    <HistoricalTableRow
                      key={date}
                      date={date}
                      entries={entries}
                      athleteId={athleteId}
                      teamId={teamId}
                    />
                  ))}
                  {groupedData.size > 0 && (
                    <HistoricalTableFooterRow groupedData={groupedData} weekRange={weekRange} />
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {!showEmptyState && totalCount > 0 && (
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          labelDisplayedRows={getLabelDisplayedRows}
        />
      )}
    </Box>
  );
};
